/** @jsxImportSource @emotion/react */
'use memo'

import { useRef } from 'react'
import styled from '@emotion/styled'
import type { ColDef, GridApi, RowHeightParams } from 'ag-grid-community'
import type { OrgRegistration } from '@sportninja/common/features/organization/registration/types'
import {
  placeDetailRowsUnderParents,
  type OrgRegistrationListRow,
} from '@sportninja/common/features/organization/registration/utils/listRows'
import DataGrid from 'src/components/ts-components/DataGrid'
import { useIsMobile } from 'src/hooks/useIsMobile'
import { DATA_GRID_TOKENS as T } from 'src/components/ts-components/DataGrid/tokens'
import { ORG_REGISTRATION_ROW_HEIGHT } from '../../constants/listLayout'
import { useRegistrationListColumns } from '../../hooks/useRegistrationListColumns'
import OrgRegistrationDetailRow, {
  type OrgRegistrationDetailRowParams,
} from './OrgRegistrationDetailRow'

interface Props {
  rows: OrgRegistrationListRow[]
  onToggle: (registrationId: string) => void
  onOpen: (registration: OrgRegistration) => void
}

/**
 * A divisions row's height before it has been measured: header rows, its
 * division rows, the note and padding. Close enough that the measured height
 * lands without a visible jump.
 */
const estimateDetailHeight = (divisionCount: number) => 160 + divisionCount * 56

/** Single-line cells centred in the two-line row, level with the rest. */
const CENTERED_CELL: ColDef<OrgRegistrationListRow> = {
  cellStyle: { display: 'flex', alignItems: 'center' },
}

/**
 * The list view: one sortable row per registration, with its divisions table
 * opening in a full-width row underneath. AG Grid Community has no
 * master/detail, so the detail is a row of its own — re-seated under its
 * parent after every sort, and sized to its content once measured.
 */
const OrgRegistrationTable = ({ rows, onToggle, onOpen }: Props) => {
  'use memo'

  const isCompact = useIsMobile()
  const columnDefs = useRegistrationListColumns({ onToggle, onOpen, isCompact })
  const apiRef = useRef<GridApi<OrgRegistrationListRow> | null>(null)
  // Measured heights outlive a row's data being replaced (a page arriving
  // rebuilds every row), which would otherwise reset it to the estimate.
  const detailHeights = useRef(new Map<string, number>())

  const getRowHeight = ({ data }: RowHeightParams<OrgRegistrationListRow>) =>
    data?.kind === 'detail'
      ? detailHeights.current.get(data.id) ??
        estimateDetailHeight(data.registration.divisions.length)
      : ORG_REGISTRATION_ROW_HEIGHT

  const detailRowParams: OrgRegistrationDetailRowParams = {
    onHeight: (rowId, height) => {
      detailHeights.current.set(rowId, height)
      const node = apiRef.current?.getRowNode(rowId)
      if (node && node.rowHeight !== height) {
        node.setRowHeight(height)
        apiRef.current?.onRowHeightChanged()
      }
    },
  }

  return (
    <Frame>
      <DataGrid<OrgRegistrationListRow>
        rowData={rows}
        columnDefs={columnDefs}
        getRowId={({ data }) => data.id}
        defaultColDef={CENTERED_CELL}
        domLayout='autoHeight'
        // The list shows its own empty state, so the grid never renders empty.
        autoHeightMinBodyHeight={0}
        getRowHeight={getRowHeight}
        isFullWidthRow={({ rowNode }) => rowNode.data?.kind === 'detail'}
        fullWidthCellRenderer={OrgRegistrationDetailRow}
        fullWidthCellRendererParams={detailRowParams}
        postSortRows={({ nodes }) => {
          const ordered = placeDetailRowsUnderParents(
            nodes,
            (node) => node.data
          )
          nodes.splice(0, nodes.length, ...ordered)
        }}
        onGridReady={({ api }) => {
          apiRef.current = api
        }}
        suppressCellFocus
        horizontalScrollAffordance
        // As on the Operations grids: the scrollbar stays at the bottom of the
        // screen while rows run past it, not only under the last row.
        stickyHorizontalScrollbar
      />
    </Frame>
  )
}

export default OrgRegistrationTable

const Frame = styled.div`
  width: 100%;
  min-width: 0;

  /* rowHeight sizes the row box only; cell line-height follows the theme's
     --ag-row-height, so it is matched here or text sits high. Declared on
     .ag-root-wrapper because the theme sets it on an element below.
     No zebra: an opened divisions row would shift every row under it onto
     the other stripe, so rows keep one surface and the hairlines divide them. */
  .ag-root-wrapper {
    --ag-row-height: ${ORG_REGISTRATION_ROW_HEIGHT}px;
    --ag-odd-row-background-color: ${T.background};
  }

  /* The divisions row brings its own recessed surface; no hover tint over it. */
  .ag-full-width-row,
  .ag-full-width-row.ag-row-hover {
    background: ${T.oddRowBackground};
  }
`
